import type { BaseValidation } from '@/components/datamodel/types';
import type { AssetApiRecord } from '@/pages/dam/api/assets.types';
import {
    type AttributeMessages,
    type AttributeStates,
    groupMessagesByAttribute,
    parseAttributeStates,
} from '@/pages/dam/utils/assetDataState';

interface DamValidationMessages {
    errors: AttributeMessages;
    warnings: AttributeMessages;
}

type ValidationsByAttribute = Record<string, BaseValidation[]>;

const isValidationsByAttribute = (value: unknown): value is ValidationsByAttribute =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

type TranslateMessage = (validation: BaseValidation, attributeName: string) => string;

/** Messages per attribute, translated like the item editor's field messages (`translateValidationMessage`). */
const groupValidations = (validations: ValidationsByAttribute, translateMessage: TranslateMessage): DamValidationMessages => {
    const errors: AttributeMessages = {};
    const warnings: AttributeMessages = {};
    for (const [attributeName, entries] of Object.entries(validations)) {
        for (const entry of entries ?? []) {
            const target = entry.level === 'Warning' ? warnings : errors;
            (target[attributeName] ??= []).push(translateMessage(entry, attributeName));
        }
    }
    return { errors, warnings };
};

/**
 * Messages of the stored validation per attribute (`''` = the asset as a whole): `__validations__`
 * (`GET …?validate=true`, D-051) through `translateMessage`, otherwise the stored rule keys of
 * `validation__` and `validation_warnings__` through `translateRuleKey`.
 */
const resolveDamValidationMessages = (
    asset: AssetApiRecord,
    translateRuleKey: (ruleKey: string, attributeName?: string) => string,
    translateMessage: TranslateMessage,
): DamValidationMessages => {
    const validations = asset.__validations__;
    if (isValidationsByAttribute(validations)) {
        return groupValidations(validations, translateMessage);
    }

    const translate = (grouped: AttributeMessages): AttributeMessages => Object.fromEntries(
        Object.entries(grouped).map(([attributeName, keys]) =>
            [attributeName, keys.map((key) => translateRuleKey(key, attributeName || undefined))]),
    );
    return {
        errors: translate(groupMessagesByAttribute(asset.validation__ as string[] | undefined)),
        warnings: translate(groupMessagesByAttribute(asset.validation_warnings__ as string[] | undefined)),
    };
};

/** Hidden / read-only states per attribute from `__attributeStates__`, otherwise from `attributeStates__`. */
const resolveDamAttributeStates = (asset: AssetApiRecord): AttributeStates => {
    const states = asset.__attributeStates__;
    if (typeof states === 'object' && states !== null && !Array.isArray(states)) {
        return Object.fromEntries(Object.entries(states as Record<string, string[]>)
            .map(([attributeName, list]) => [attributeName, new Set(list)]));
    }
    return parseAttributeStates(asset.attributeStates__ as string[] | undefined);
};

const toAttributeStateLists = (states: AttributeStates): Record<string, string[]> =>
    Object.fromEntries(Object.entries(states).map(([attributeName, set]) => [attributeName, [...set]]));

/** Start state of the live validation: `__validations__` as delivered, otherwise built from the resolved messages. */
const toInitialValidations = (asset: AssetApiRecord, messages: DamValidationMessages): ValidationsByAttribute => {
    if (isValidationsByAttribute(asset.__validations__)) {
        return asset.__validations__;
    }
    const initial: ValidationsByAttribute = {};
    const add = (grouped: AttributeMessages, level: BaseValidation['level']) => {
        for (const [attributeName, texts] of Object.entries(grouped)) {
            if (!attributeName) {
                continue;
            }
            for (const message of texts) {
                (initial[attributeName] ??= []).push({ name: message, message, level, path: [attributeName] });
            }
        }
    };
    add(messages.errors, 'Error');
    add(messages.warnings, 'Warning');
    return initial;
};

export { groupValidations, resolveDamAttributeStates, resolveDamValidationMessages, toAttributeStateLists, toInitialValidations };
export type { DamValidationMessages, TranslateMessage, ValidationsByAttribute };
